import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

export function useUserPersistentState<T>(
  key: string,
  initialValue: T,
  options: {
    debounceMs?: number;
    serializer?: {
      serialize: (value: T) => string;
      deserialize: (value: string) => T;
    };
  } = {}
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const { debounceMs = 500, serializer } = options;

  const userId = useSelector((state: RootState) => state.user?.user?.id);

  const userSpecificKey = userId ? `${key}_user_${userId}` : `${key}_anonymous`;

  const defaultSerializer = {
    serialize: JSON.stringify,
    deserialize: JSON.parse
  };

  const { serialize, deserialize } = serializer || defaultSerializer;

  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue;

    try {
      const item = localStorage.getItem(userSpecificKey);
      if (item) {
        return deserialize(item);
      }
    } catch (error) {
      console.warn(`Failed to load persisted state for key "${userSpecificKey}":`, error);
      // Clean up corrupted data
      localStorage.removeItem(userSpecificKey);
    }

    return initialValue;
  });

  const [saveTimeoutId, setSaveTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const saveToStorage = useCallback((value: T) => {
    if (typeof window === 'undefined' || !userId) return;

    try {
      localStorage.setItem(userSpecificKey, serialize(value));
    } catch (error) {
      console.warn(`Failed to save state to localStorage for key "${userSpecificKey}":`, error);
    }
  }, [userSpecificKey, userId, serialize]);

  const updateState = useCallback((value: T | ((prev: T) => T)) => {
    setState(prevState => {
      const newState = typeof value === 'function' ? (value as (prev: T) => T)(prevState) : value;

      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }

      const timeoutId = setTimeout(() => {
        saveToStorage(newState);
        setSaveTimeoutId(null);
      }, debounceMs);

      setSaveTimeoutId(timeoutId);

      return newState;
    });
  }, [saveTimeoutId, saveToStorage, debounceMs]);

  const clearPersistedState = useCallback(() => {
    if (typeof window === 'undefined' || !userId) return;

    try {
      localStorage.removeItem(userSpecificKey);
      setState(initialValue);
    } catch (error) {
      console.warn(`Failed to clear persisted state for key "${userSpecificKey}":`, error);
    }
  }, [userSpecificKey, userId, initialValue]);

  useEffect(() => {
    return () => {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }
    };
  }, [saveTimeoutId]);

  useEffect(() => {
    if (userId) {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
        setSaveTimeoutId(null);
      }
      saveToStorage(state);
    }
  }, [userId]);

  return [state, updateState, clearPersistedState];
}